/**
 * SuggestedPrompts component (REQ-D-046).
 *
 * Static empty-state starter pills shown on first load, BEFORE the
 * patient types anything. Distinct from `SuggestedActions`:
 *
 *   - SuggestedActions  = backend-confidence-driven runtime pills
 *                         rendered below a bot message.
 *   - SuggestedPrompts  = static, per-entity starter prompts from
 *                         ENTITY_PRESETS[entityId].suggestedPrompts,
 *                         rendered once in the welcome/empty state.
 *
 * Tapping a pill sends its text as the FIRST user turn through the
 * widget's normal send path, then the whole pill row collapses (the
 * empty state is over once the conversation has started).
 *
 * Styling reuses the existing `.hana-action-btn` design token so the
 * pills match the runtime SuggestedActions look per entity branding.
 * The container uses a DEDICATED `.hana-suggested-prompts` class
 * (NOT the shared `.hana-suggested-actions`) so empty-state pills and
 * runtime action pills stay independently selectable.
 */

import type { ResolvedWidgetConfig } from "../types";

export interface SuggestedPromptsOptions {
  config: ResolvedWidgetConfig;
  /** Called when a starter pill is tapped. Receives the prompt text. */
  onSelect: (prompt: string) => void;
}

/** Cap so a misconfigured preset never floods the empty state. */
const MAX_PROMPTS = 5;

export class SuggestedPrompts {
  private readonly element: HTMLElement;
  private readonly onSelect: (prompt: string) => void;
  private dismissed = false;

  constructor(options: SuggestedPromptsOptions) {
    this.onSelect = options.onSelect;

    const prompts = (options.config.branding.suggestedPrompts ?? [])
      .map((p) => p.trim())
      .filter((p) => p.length > 0)
      .slice(0, MAX_PROMPTS);

    this.element = document.createElement("div");
    this.element.className = "hana-suggested-prompts";
    this.element.setAttribute("role", "group");
    this.element.setAttribute("aria-label", "Suggested starter prompts");

    for (const prompt of prompts) {
      this.element.appendChild(this.createPill(prompt));
    }
  }

  /** Whether this entity actually has starter prompts to render. */
  hasPrompts(): boolean {
    return this.element.childElementCount > 0;
  }

  getElement(): HTMLElement {
    return this.element;
  }

  /** Remove the pills once the conversation has started. */
  dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    while (this.element.firstChild) {
      this.element.removeChild(this.element.firstChild);
    }
    this.element.remove();
  }

  private createPill(prompt: string): HTMLButtonElement {
    const pill = document.createElement("button");
    pill.className = "hana-action-btn hana-suggested-prompt";
    pill.setAttribute("type", "button");
    pill.setAttribute("data-prompt", prompt);
    pill.textContent = prompt;

    pill.addEventListener("click", () => {
      if (this.dismissed) return;
      this.onSelect(prompt);
      this.dismiss();
    });

    return pill;
  }
}
